import { Headline } from '@cambridgeassessment/cambridge-ui';
import { AssessmentState, BUSINESS_UNITS, Roles } from '@cambridgeassessment/lib-ee-commons';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useQueryParams } from '../../../hooks/useQueryParams';
import { RootState } from '../../../store';
import { SearchValues } from '../../../types/RmaErrors';
import { StyledContainer } from '../Content.styles';
import SearchFields from './SearchFields';
import { StyledParagraph } from './SubmissionFailures.styles';
import * as actions from '../../../store/rmaSubmissionFailures/actions';
import * as thunks from '../../../store/rmaSubmissionFailures/thunks';
import FailedSubmissionsList from './FailedSubmissionsList';
import { isEmpty } from 'lodash';
import { Authorized } from 'lib-browser';

const SubmissionFailuresPage: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const navigateToHomePage = useCallback((): void => history.push('/'), [history]);
  const {
    userData: { businessUnit, selectedRole },
    rmaSubmissionFailures: { isRmaErrorsLoading },
  } = useSelector((state: RootState) => state);

  const queryParams = useQueryParams<SearchValues>();
  const [inputValues, setInputValues] = useState<SearchValues>({});
  const onChangeSearchValues = (searchValues: Partial<SearchValues>) => {
    setInputValues({ ...inputValues, ...searchValues });
  };

  useEffect(() => {
    const initialSearchValues = queryParams.params;
    setInputValues(initialSearchValues);

    // fill results table automatically if query params are passed
    if (!isEmpty(initialSearchValues)) {
      searchSections(initialSearchValues);
    }

    return () => {
      dispatch(actions.resetState());
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const searchSections = (searchValues: SearchValues) => {
    dispatch(actions.resetState());
    dispatch(
      thunks.getSectionsWithRmaErrors(businessUnit?.id, searchValues, [
        AssessmentState.FAILED_TO_SEND_FOR_ASSESSMENT,
        AssessmentState.READY_FOR_RM_RERUN,
      ]),
    );
  };

  const onSearchClick = () => {
    queryParams.appendQueryParams(inputValues);
    searchSections(inputValues);
  };

  useEffect(() => {
    if (businessUnit?.id !== BUSINESS_UNITS.CI.id) navigateToHomePage();
  }, [businessUnit, navigateToHomePage]);

  return (
    <Authorized roles={[Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR]} selectedRole={selectedRole}>
      <StyledContainer data-qa-id='page-priming-rm-submission'>
        <Headline>RM file submission failures</Headline>
        <StyledParagraph>Search for and resend RM file submission failures.</StyledParagraph>
        <SearchFields
          onChangeSearchValues={onChangeSearchValues}
          onSearch={onSearchClick}
          inputValues={inputValues}
          searchDisabled={!inputValues.seriesId || isRmaErrorsLoading}
        />
        <FailedSubmissionsList inputValues={queryParams.params} />
      </StyledContainer>
    </Authorized>
  );
};

export default SubmissionFailuresPage;
